"use client";

// Le tour d'un agent, en direct, hors du chat (app/en_direct.py) : ce que l'agent du projet fait pendant la mission
// que l'Orchestrateur lui a confiée, ou pendant un tour qu'un autre agent a demandé dans le fil ouvert. ChatKit ne
// montre un tour qu'à qui l'a lancé ; ici, on voit ses étapes et la fin du message qu'il écrit, à mesure.

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { cleFil, enDirect, type Direct } from "@/lib/api";
import { Grue } from "./Orchestrateur";
import "./en-direct.css";

// Le flux d'un fil ; il se ferme quand le tour finit (`actif: false`), et se rouvre si le fil change.
export function useDirect(fil: string | null): Direct | null {
  const [d, setD] = useState<Direct | null>(null);
  useEffect(() => {
    setD(null);
    if (!fil) return;
    const flux = new EventSource(enDirect(fil));
    flux.onmessage = (m) => {
      const suite = JSON.parse(m.data) as Direct;
      setD(suite);
      if (!suite.actif) flux.close();
    };
    return () => flux.close();
  }, [fil]);
  return d;
}

// Ouvrir le fil d'un projet : le projet le rouvre de lui-même (le fil retenu de sa portée).
export function useOuvrirFilDeProjet() {
  const router = useRouter();
  return useCallback(
    (affaire: string, fil: string) => {
      try {
        window.localStorage.setItem(
          cleFil({ genre: "projet", id: affaire }),
          fil,
        );
      } catch {
        // Sans stockage local, le projet s'ouvre sur son dernier fil.
      }
      router.push(`/projets/${affaire}`);
    },
    [router],
  );
}

function Etapes({ d }: { d: Extract<Direct, { actif: true }> }) {
  if (!d.etapes.length && !d.texte)
    return <p className="direct-attente">L&apos;agent se met en route…</p>;
  return (
    <>
      {d.etapes.length > 0 && (
        <ol className="direct-etapes">
          {d.etapes.map((e, i) => {
            const [description] = e.contenu.split("\n");
            const enCours =
              e.statut === "en cours" && i === d.etapes.length - 1;
            return (
              <li
                key={i}
                className={
                  enCours ? "en-cours" : e.statut === "faite" ? "faite" : ""
                }
              >
                <span className="direct-emoji" aria-hidden>
                  {e.emoji}
                </span>
                <span className="direct-libelle">
                  {e.libelle}
                  {description && (
                    <span className="discret"> · {description}</span>
                  )}
                </span>
                <span className="direct-statut" aria-hidden>
                  {enCours ? <i /> : e.statut === "faite" ? "✓" : ""}
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {d.texte && <Texte texte={d.texte} />}
    </>
  );
}

// La fin du message, collée en bas : on lit ce qu'il écrit à mesure.
function Texte({ texte }: { texte: string }) {
  const boite = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (boite.current) boite.current.scrollTop = boite.current.scrollHeight;
  }, [texte]);
  return (
    <div ref={boite} className="direct-texte">
      {texte}
      <span className="direct-curseur" aria-hidden />
    </div>
  );
}

// La carte d'un tour : la mission, le projet, ses étapes et son texte.
export function EnDirect({
  fil,
  titre,
  sousTitre,
  onOuvrir,
}: {
  fil: string;
  titre: string;
  sousTitre?: string;
  onOuvrir?: () => void;
}) {
  const d = useDirect(fil);
  if (d && !d.actif) return null;
  return (
    <section
      className="direct"
      aria-live="polite"
      aria-label={`${titre}, en direct`}
    >
      <header className="direct-tete">
        <Grue etat="au-travail" />
        <span className="direct-titres">
          <strong>{titre}</strong>
          {sousTitre && <span className="discret">{sousTitre}</span>}
        </span>
        <span className="direct-pastille">en direct</span>
        {onOuvrir && (
          <button type="button" className="lien" onClick={onOuvrir}>
            Ouvrir son fil
          </button>
        )}
      </header>
      {d ? <Etapes d={d} /> : <p className="direct-attente">Connexion…</p>}
    </section>
  );
}
